import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useEffect, useLayoutEffect, useState, useRef } from "react";
import { getProfile, getUserRole } from "../../service/api/apiClient";
import { getNavForRole } from "../../utils/routeConfig";
import "../../css/Layout.css";
import { ChevronDown, SignOutIcon } from "../../utils/icons";

export const MainHeader = () => {
    const navigate = useNavigate();
    const [openMenu, setOpenMenu]     = useState(null);
    const [userOpen, setUserOpen]     = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const [headerHeight, setHeaderHeight] = useState(60);
    const shellRef  = useRef(null);
    const headerRef = useRef(null); // measures the whole <header> (top row + nav row)

    const profile        = getProfile();
    const user           = profile?.user || {};
    const role           = getUserRole();
    const normalizedRole = role?.toLowerCase().replace(/\s+/g, "_");
    const initials       = user?.name
        ? user.name.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase()
        : "U";

    const filteredNav = getNavForRole(normalizedRole);

    const handleSignOut = () => { localStorage.clear(); navigate("/signin"); };
    const closeAll      = () => { setOpenMenu(null); setUserOpen(false); setMobileOpen(false); };

    useEffect(() => {
        const handler = (e) => {
            if (shellRef.current && !shellRef.current.contains(e.target)) closeAll();
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    useEffect(() => {
        document.body.style.overflow = mobileOpen ? "hidden" : "";
        return () => { document.body.style.overflow = ""; };
    }, [mobileOpen]);

    // Keep dropdown/drawer offset in sync with real header height
    // (header can now be 1 or 2 rows tall depending on nav wrapping)
    useLayoutEffect(() => {
        if (!headerRef.current) return;
        const el = headerRef.current;
        const update = () => setHeaderHeight(el.offsetHeight);
        update();
        const ro = new ResizeObserver(update);
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    // ── Shared renderers ──────────────────────────────────────────────────────

    const renderLeaf = (item, mobile = false) => (
        <NavLink
            key={item.id}
            to={item.to}
            end={item.end}
            className={({ isActive }) =>
                mobile
                    ? `mh-mobile-item${isActive ? " active" : ""}`
                    : `mh-nav-item${isActive ? " active" : ""}`
            }
            onClick={mobile ? closeAll : () => setOpenMenu(null)}
        >
            {mobile && <span className="mh-mobile-item__icon">{item.icon}</span>}
            {!mobile && item.icon}
            <span>{item.label}</span>
        </NavLink>
    );

    const renderDropdown = (item, mobile = false) => {
        const isOpen = openMenu === item.id;
        const toggle = () => setOpenMenu(isOpen ? null : item.id);

        if (mobile) {
            return (
                <div key={item.id}>
                    <button
                        className={`mh-mobile-item mh-mobile-parent${isOpen ? " open" : ""}`}
                        onClick={toggle}
                    >
                        <span className="mh-mobile-item__icon">{item.icon}</span>
                        <span>{item.label}</span>
                        <span className={`mh-chevron${isOpen ? " rotated" : ""}`} style={{ marginLeft: "auto" }}>
                            <ChevronDown />
                        </span>
                    </button>
                    {isOpen && (
                        <div className="mh-mobile-children">
                            {item.children.map((child) => (
                                <NavLink
                                    key={child.to}
                                    to={child.to}
                                    className={({ isActive }) => `mh-mobile-child${isActive ? " active" : ""}`}
                                    onClick={closeAll}
                                >
                                    <span className="mh-mobile-item__icon">{child.icon}</span>
                                    <span>{child.label}</span>
                                </NavLink>
                            ))}
                        </div>
                    )}
                </div>
            );
        }

        return (
            <div
                key={item.id}
                className="mh-nav-group"
                onMouseEnter={() => setOpenMenu(item.id)}
                onMouseLeave={() => setOpenMenu(null)}
            >
                <button
                    className={`mh-nav-item mh-nav-parent${isOpen ? " open" : ""}`}
                    onClick={toggle}
                >
                    {item.icon}
                    <span>{item.label}</span>
                    <span className={`mh-chevron${isOpen ? " rotated" : ""}`}><ChevronDown /></span>
                </button>
                {isOpen && (
                    <div className="mh-mega-dropdown" style={{ top: headerHeight }}>
                        <div className="mh-mega-inner">
                            {item.children.map((child) => (
                                <NavLink
                                    key={child.to}
                                    to={child.to}
                                    className={({ isActive }) => `mh-mega-item${isActive ? " active" : ""}`}
                                    onClick={() => setOpenMenu(null)}
                                >
                                    <span className="mh-mega-item__icon">{child.icon}</span>
                                    <span className="mh-mega-item__text">
                                        <span className="mh-mega-item__label">{child.label}</span>
                                        {child.desc && <span className="mh-mega-item__desc">{child.desc}</span>}
                                    </span>
                                </NavLink>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    const renderNavItem = (item, mobile = false) =>
        item.children ? renderDropdown(item, mobile) : renderLeaf(item, mobile);

    // ─────────────────────────────────────────────────────────────────────────

    return (
        <div className={`mh-shell${openMenu ? " nav-open" : ""}`} ref={shellRef}>
            <header className="mh-header" ref={headerRef}>

                {/* Row 1: brand + user + hamburger */}
                <div className="mh-header-top">
                    <a href="/" className="mh-brand">
                        <img alt="Logo" src="https://food.totalfresh.co/assets/img/logos/logo.png"
                             style={{ width: 135, height: 40 }} />
                    </a>

                    <div className="mh-header-right">
                        <div style={{ position: "relative" }}>
                            <button
                                className={`mh-user-btn${userOpen ? " open" : ""}`}
                                onClick={() => { setUserOpen((o) => !o); setMobileOpen(false); }}
                            >
                                <div className="mh-user__avatar">{initials}</div>
                                <span className="mh-user__name">{user.name || "User"}</span>
                                <span className="mh-user__chevron"><ChevronDown /></span>
                            </button>
                            {userOpen && (
                                <div className="mh-dropdown">
                                    <div className="mh-dropdown__header">
                                        <div className="mh-dropdown__name">{user.name || "User"}</div>
                                        <div className="mh-dropdown__role">{role}</div>
                                    </div>
                                    <button className="mh-dropdown__item" onClick={handleSignOut}>
                                        <SignOutIcon /> Sign Out
                                    </button>
                                </div>
                            )}
                        </div>

                        <button
                            className={`mh-hamburger${mobileOpen ? " is-open" : ""}`}
                            onClick={() => { setMobileOpen((o) => !o); setUserOpen(false); }}
                            aria-label="Toggle menu"
                        >
                            <span /><span /><span />
                        </button>
                    </div>
                </div>

                {/* Row 2: nav links, wraps instead of horizontal-scrolling */}
                <nav className="mh-nav-row">
                    {filteredNav.map((item) => renderNavItem(item, false))}
                </nav>
            </header>

            {/* Mobile Drawer */}
            {mobileOpen && <div className="mh-mobile-backdrop" style={{ top: headerHeight }} onClick={closeAll} />}
            <div className={`mh-mobile-drawer${mobileOpen ? " is-open" : ""}`} style={{ top: headerHeight }}>
                <nav className="mh-mobile-nav">
                    {filteredNav.map((item) => renderNavItem(item, true))}
                </nav>
            </div>

            <main className="mh-main">
                <Outlet />
            </main>
        </div>
    );
};